import React, { useState } from 'react';
import { 
  User, 
  FileText, 
  Upload, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  MessageSquare,
  Calendar,
  Download,
  Eye,
  Send,
  Search,
  Paperclip,
  Phone,
  Video,
  Settings,
  LogOut
} from 'lucide-react';
import { Document, DocumentRequirement, Message } from '../../types';
import { Proposal } from '../../types';
import { mockProposals } from '../../data/mockData';
import { useAuth } from '../../contexts/AuthContext';

const StudentDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'applications' | 'messages' | 'settings'>('overview');
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Öğrenciye gönderilen teklifler
  const studentProposals = mockProposals.filter(proposal => 
    proposal.studentId === user?.id
  );

  // Mock data - gerçek uygulamada API'den gelecek
  const studentInfo = {
    name: 'Zeynep Kara',
    email: 'zeynep.kara@email.com',
    phone: '+90 555 123 4567',
    studentId: 'PE2024001',
    consultant: 'Fatma Özkan',
    consultantEmail: 'egitim@pisaeducazione.com',
    status: 'Aktif Başvuru Süreci',
    interestedCountries: ['İtalya'],
    interestedPrograms: ['Mühendislik', 'Bilgisayar Bilimleri']
  };

  const documentRequirements: DocumentRequirement[] = [
    {
      id: '1',
      name: 'Pasaport',
      type: 'passport',
      description: 'Geçerli pasaportunuzun tüm sayfalarının renkli taraması',
      isRequired: true,
      acceptedFormats: ['PDF', 'JPG', 'PNG'],
      maxSize: 5,
      examples: ['Pasaport ana sayfası', 'Tüm damgalı sayfalar']
    },
    {
      id: '2',
      name: 'Diploma',
      type: 'diploma',
      description: 'Mezun olduğunuz okul diplomasının noter onaylı kopyası',
      isRequired: true,
      acceptedFormats: ['PDF'],
      maxSize: 10,
      examples: ['Lise diploması', 'Üniversite diploması']
    },
    {
      id: '3',
      name: 'Transkript',
      type: 'transcript',
      description: 'Tüm dönemlerin not dökümü (İngilizce çeviri ile)',
      isRequired: true,
      acceptedFormats: ['PDF'],
      maxSize: 10
    },
    {
      id: '4',
      name: 'Dil Belgesi',
      type: 'language-certificate',
      description: 'IELTS, TOEFL veya eşdeğer dil yeterlilik belgesi',
      isRequired: true,
      acceptedFormats: ['PDF'],
      maxSize: 5
    },
    {
      id: '5',
      name: 'CV',
      type: 'cv',
      description: 'Güncel özgeçmiş (Europass formatı tercih edilir)',
      isRequired: true,
      acceptedFormats: ['PDF', 'DOC', 'DOCX'],
      maxSize: 3
    },
    {
      id: '6',
      name: 'Motivasyon Mektubu',
      type: 'motivation-letter',
      description: 'Neden bu programı seçtiğinizi açıklayan mektup',
      isRequired: true,
      acceptedFormats: ['PDF', 'DOC', 'DOCX'],
      maxSize: 3
    },
    {
      id: '7',
      name: 'Fotoğraf',
      type: 'photo',
      description: 'Biyometrik fotoğraf (3.5x4.5 cm, beyaz zemin)',
      isRequired: true,
      acceptedFormats: ['JPG', 'PNG'],
      maxSize: 1
    },
    {
      id: '8',
      name: 'Mali Durum Belgesi',
      type: 'financial-document',
      description: 'Banka hesap özeti veya sponsor mektubu',
      isRequired: false,
      acceptedFormats: ['PDF'],
      maxSize: 5
    }
  ];

  const uploadedDocuments: Document[] = [
    {
      id: '1',
      studentId: 'student-1',
      name: 'Pasaport_ZeynepKara.pdf',
      type: 'passport',
      url: '/documents/passport.pdf',
      uploadedBy: 'student-1',
      uploadedAt: new Date('2024-01-15'),
      status: 'approved',
      isRequired: true,
      reviewedBy: 'Fatma Özkan',
      reviewedAt: new Date('2024-01-16')
    },
    {
      id: '2',
      studentId: 'student-1',
      name: 'Diploma_ZeynepKara.pdf',
      type: 'diploma',
      url: '/documents/diploma.pdf',
      uploadedBy: 'student-1',
      uploadedAt: new Date('2024-01-18'),
      status: 'pending',
      isRequired: true
    },
    {
      id: '3',
      studentId: 'student-1',
      name: 'CV_ZeynepKara.pdf',
      type: 'cv',
      url: '/documents/cv.pdf',
      uploadedBy: 'student-1',
      uploadedAt: new Date('2024-01-20'),
      status: 'revision-required',
      isRequired: true,
      notes: 'Lütfen iş deneyimi bölümünü daha detaylandırın',
      reviewedBy: 'Fatma Özkan',
      reviewedAt: new Date('2024-01-21')
    }
  ];

  // Mock mesajlar
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      senderId: 'consultant-1',
      senderName: 'Fatma Özkan',
      receiverId: 'student-1',
      content: 'Merhaba Zeynep, pasaport belgeniz onaylandı. Diploma belgenizi de inceliyoruz, yakında geri dönüş yapacağız.',
      type: 'text',
      isRead: true,
      createdAt: new Date('2024-01-16T10:30:00')
    },
    {
      id: '2',
      senderId: 'student-1',
      senderName: 'Zeynep Kara',
      receiverId: 'consultant-1',
      content: 'Teşekkür ederim. CV\'mde hangi bölümü detaylandırmam gerekiyor?',
      type: 'text',
      isRead: true,
      createdAt: new Date('2024-01-21T14:15:00')
    },
    {
      id: '3',
      senderId: 'consultant-1',
      senderName: 'Fatma Özkan',
      receiverId: 'student-1',
      content: 'İş deneyimi bölümünde kullandığınız teknolojileri ve projelerde aldığınız sorumlulukları daha detaylı açıklayabilirsiniz.',
      type: 'text',
      isRead: true,
      createdAt: new Date('2024-01-21T14:45:00')
    },
    {
      id: '4',
      senderId: 'student-1',
      senderName: 'Zeynep Kara',
      receiverId: 'consultant-1',
      content: 'Anladım, CV\'mi güncelleyip tekrar yükleyeceğim. Teşekkürler!',
      type: 'text',
      isRead: false,
      createdAt: new Date('2024-01-21T15:00:00')
    }
  ]);

  const getDocumentStatus = (requirement: DocumentRequirement) => {
    const uploaded = uploadedDocuments.find(doc => doc.type === requirement.type);
    if (!uploaded) return 'missing';
    return uploaded.status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'revision-required': return 'text-orange-600 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400';
      case 'rejected': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      case 'missing': return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Onaylandı';
      case 'pending': return 'İnceleniyor';
      case 'revision-required': return 'Düzeltme Gerekli';
      case 'rejected': return 'Reddedildi';
      case 'missing': return 'Yüklenmedi';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5" />;
      case 'pending': return <Clock className="w-5 h-5" />;
      case 'revision-required': return <AlertTriangle className="w-5 h-5" />;
      case 'rejected': return <AlertTriangle className="w-5 h-5" />;
      case 'missing': return <Upload className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const handleFileUpload = (requirement: DocumentRequirement) => {
    // Dosya yükleme işlemi
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = requirement.acceptedFormats.map(format => `.${format.toLowerCase()}`).join(',');
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Dosya boyutu kontrolü
        if (file.size > requirement.maxSize * 1024 * 1024) {
          alert(`Dosya boyutu ${requirement.maxSize}MB'dan büyük olamaz`);
          return;
        }
        
        // Dosya formatı kontrolü
        const fileExtension = file.name.split('.').pop()?.toUpperCase();
        if (!requirement.acceptedFormats.includes(fileExtension || '')) {
          alert(`Sadece ${requirement.acceptedFormats.join(', ')} formatları kabul edilir`);
          return;
        }
        
        // Burada dosya yükleme API'si çağrılacak
        console.log('Dosya yükleniyor:', file.name, 'Tip:', requirement.type);
        alert('Dosya başarıyla yüklendi! İnceleme için danışmanınıza gönderildi.');
      }
    };
    input.click();
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      senderId: 'student-1',
      senderName: 'Zeynep Kara',
      receiverId: 'consultant-1',
      content: messageText.trim(),
      type: 'text',
      isRead: false,
      createdAt: new Date()
    };

    setMessages([...messages, newMessage]);
    setMessageText('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredMessages = messages.filter(message => 
    message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.senderName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Şimdi';
    if (minutes < 60) return `${minutes} dk önce`;
    if (hours < 24) return `${hours} saat önce`;
    if (days < 7) return `${days} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Öğrenci Bilgileri */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Öğrenci Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ad Soyad</p>
            <p className="font-medium text-gray-900 dark:text-white">{studentInfo.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Öğrenci No</p>
            <p className="font-medium text-gray-900 dark:text-white">{studentInfo.studentId}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
            <p className="font-medium text-gray-900 dark:text-white">{studentInfo.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Telefon</p>
            <p className="font-medium text-gray-900 dark:text-white">{studentInfo.phone}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Danışman</p>
            <p className="font-medium text-gray-900 dark:text-white">{studentInfo.consultant}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Durum</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
              {studentInfo.status}
            </span>
          </div>
        </div>
      </div>

      {/* Yaklaşan Deadline'lar */}
      {urgentDeadlines.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-300">Yaklaşan Deadline'lar</h3>
          </div>
          <div className="space-y-3">
            {urgentDeadlines.slice(0, 3).map((deadline) => {
              const daysLeft = getDaysLeft(deadline.dueDate);
              return (
                <div key={deadline.id} className="bg-white dark:bg-gray-900 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{deadline.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{deadline.description}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Son Tarih: {format(deadline.dueDate, 'dd MMMM yyyy', { locale: tr })}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      daysLeft <= 3 ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                      daysLeft <= 7 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                      {daysLeft <= 0 ? 'Süresi Doldu' : `${daysLeft} gün kaldı`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* İlgi Alanları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hedef Ülke</h3>
          <div className="space-y-2">
            {studentInfo.interestedCountries.map((country, index) => (
              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 mr-2">
                {country}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">İlgilendiğim Programlar</h3>
          <div className="space-y-2">
            {studentInfo.interestedPrograms.map((program, index) => (
              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 mr-2">
                {program}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Evrak Durumu Özeti */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Evrak Durumu Özeti</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {uploadedDocuments.filter(doc => doc.status === 'approved').length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Onaylanan</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {uploadedDocuments.filter(doc => doc.status === 'pending').length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">İnceleniyor</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {uploadedDocuments.filter(doc => doc.status === 'revision-required').length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Düzeltme Gerekli</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
              <Upload className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {documentRequirements.filter(req => getDocumentStatus(req) === 'missing').length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Eksik</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Önemli Bilgi</h4>
            <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
              Tüm belgelerinizi yükledikten sonra danışmanınız tarafından incelenecektir. 
              Eksik veya hatalı belgeler için size geri bildirim verilecektir.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        {documentRequirements.map((requirement) => {
          const status = getDocumentStatus(requirement);
          const uploadedDoc = uploadedDocuments.find(doc => doc.type === requirement.type);
          
          return (
            <div key={requirement.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {requirement.name}
                      {requirement.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </h3>
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                      {getStatusIcon(status)}
                      <span>{getStatusText(status)}</span>
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                    {requirement.description}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-500">
                    <span>Kabul edilen formatlar: {requirement.acceptedFormats.join(', ')}</span>
                    <span>•</span>
                    <span>Maksimum boyut: {requirement.maxSize}MB</span>
                  </div>
                  {requirement.examples && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Örnekler: {requirement.examples.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {uploadedDoc ? (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{uploadedDoc.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Yüklendi: {uploadedDoc.uploadedAt.toLocaleDateString('tr-TR')}
                        </p>
                        {uploadedDoc.reviewedAt && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            İncelendi: {uploadedDoc.reviewedAt.toLocaleDateString('tr-TR')} - {uploadedDoc.reviewedBy}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {uploadedDoc.notes && (
                    <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                      <p className="text-sm text-orange-800 dark:text-orange-400">
                        <strong>Danışman Notu:</strong> {uploadedDoc.notes}
                      </p>
                    </div>
                  )}
                  
                  {(status === 'revision-required' || status === 'rejected') && (
                    <button
                      onClick={() => handleFileUpload(requirement)}
                      className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Yeni Dosya Yükle</span>
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleFileUpload(requirement)}
                  className="w-full border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Dosya Yükle</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      Tıklayın veya dosyayı sürükleyin
                    </p>
                  </div>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderApplications = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Başvuru Durumum</h3>
        <div className="text-center py-8">
          <Calendar className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz Başvuru Yapılmadı</h4>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Evraklarınız tamamlandıktan sonra danışmanınız başvuru sürecini başlatacaktır.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-400">
              Başvuru sürecini hızlandırmak için eksik evraklarınızı tamamlayın.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMessages = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {/* Mesaj Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">{studentInfo.consultant}</h3>
                <p className="text-sm text-blue-100">Eğitim Danışmanı</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Phone className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Video className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Arama */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Mesajlarda ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Mesajlar */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {filteredMessages.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm ? 'Arama kriterinize uygun mesaj bulunamadı' : 'Henüz mesaj yok'}
              </p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.senderId === 'student-1' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.senderId === 'student-1'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <p
                    className={`text-xs mt-1 ${
                      message.senderId === 'student-1'
                        ? 'text-blue-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {formatMessageTime(message.createdAt)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Mesaj Gönderme */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-end space-x-3">
            <button className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
              <Paperclip className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Mesajınızı yazın..."
                rows={2}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!messageText.trim()}
              className={`p-2 rounded-lg transition-colors ${
                messageText.trim()
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profil Bilgileri</h3>
        
        <div className="flex items-center space-x-6 mb-6">
          <div className="relative">
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt="Profil fotoğrafı"
                className="w-20 h-20 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-semibold">
                  {studentInfo.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              id="student-avatar-upload"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    alert('Dosya boyutu 2MB\'dan büyük olamaz');
                    return;
                  }
                  if (!file.type.startsWith('image/')) {
                    alert('Sadece resim dosyaları yüklenebilir');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const avatarUrl = e.target?.result as string;
                    // Öğrenci için updateUser fonksiyonu kullan
                    console.log('Öğrenci profil fotoğrafı güncellendi:', avatarUrl);
                    alert('Profil fotoğrafı başarıyla güncellendi!');
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="hidden"
            />
            <button 
              onClick={() => document.getElementById('student-avatar-upload')?.click()}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Fotoğraf Yükle</span>
            </button>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">JPG, PNG veya GIF. Maksimum 2MB.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ad Soyad
            </label>
            <input
              type="text"
              value={studentInfo.name}
              readOnly
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Adresi
            </label>
            <input
              type="email"
              value={studentInfo.email}
              readOnly
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon
            </label>
            <input
              type="tel"
              value={studentInfo.phone}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dil
            </label>
            <select className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
              <option value="it">Italiano</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Şifre Değiştir</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mevcut Şifre
              </label>
              <input
                type="password"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Yeni Şifre
              </label>
              <input
                type="password"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors">
            <Settings className="w-4 h-4" />
            <span>Kaydet</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bildirim Tercihleri</h3>
        
        <div className="space-y-4">
          {[
            { id: 'email_notifications', label: 'Email Bildirimleri', description: 'Önemli güncellemeler için email al' },
            { id: 'sms_notifications', label: 'SMS Bildirimleri', description: 'Acil durumlar için SMS al' },
            { id: 'document_updates', label: 'Belge Güncellemeleri', description: 'Belgeleriniz incelendiğinde bildirim al' },
            { id: 'application_updates', label: 'Başvuru Güncellemeleri', description: 'Başvuru durumu değiştiğinde bildirim al' },
            { id: 'deadlines', label: 'Deadline\'lar', icon: Clock, badge: urgentDeadlines.length },
            { id: 'meeting_reminders', label: 'Görüşme Hatırlatmaları', description: 'Yaklaşan görüşmeler için hatırlatma al' }
          ].map((setting) => (
            <div key={setting.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">{setting.label}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{setting.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Öğrenci Portalı</h1>
                <p className="text-gray-600 dark:text-gray-400">Hoş geldiniz, {studentInfo.name}</p>
              </div>
            </div>
            <button 
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Genel Bakış', icon: User },
              { id: 'documents', label: 'Evraklarım', icon: FileText },
              { id: 'applications', label: 'Başvurularım', icon: Calendar },
              { id: 'proposals', label: 'Tekliflerim', icon: FileText, badge: studentProposals.filter(p => p.status === 'sent' || p.status === 'viewed').length },
              { id: 'messages', label: 'Mesajlar', icon: MessageSquare },
              { id: 'settings', label: 'Ayarlar', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-4 py-4 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <div className="flex items-center justify-between flex-1">
                    <span className="font-medium">{tab.label}</span>
                    {tab.badge && tab.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center">
                        {tab.badge}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'applications' && renderApplications()}
        {activeTab === 'proposals' && renderProposals()}
        {activeTab === 'messages' && renderMessages()}
        {activeTab === 'deadlines' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Deadline'larım</h2>
              <p className="text-gray-600 dark:text-gray-400">Size özel önemli tarihler ve deadline'lar</p>
            </div>
            
            {urgentDeadlines.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center">
                <Clock className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Yaklaşan deadline yok</h3>
                <p className="text-gray-600 dark:text-gray-400">Şu an için acil deadline'ınız bulunmuyor.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {urgentDeadlines.map((deadline) => {
                  const daysLeft = getDaysLeft(deadline.dueDate);
                  return (
                    <div key={deadline.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{deadline.title}</h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-3">{deadline.description}</p>
                          
                          {deadline.actionRequired && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3">
                              <p className="text-sm text-blue-800 dark:text-blue-400">
                                <strong>Yapmanız Gereken:</strong> {deadline.actionRequired}
                              </p>
                            </div>
                          )}
                          
                          {deadline.requirements && deadline.requirements.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Gereksinimler:</p>
                              <ul className="space-y-1">
                                {deadline.requirements.map((req, index) => (
                                  <li key={index} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {deadline.contactPerson && (
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-500">
                              <div className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>İletişim: {deadline.contactPerson}</span>
                              </div>
                              {deadline.contactEmail && (
                                <div className="flex items-center space-x-1">
                                  <Mail className="w-4 h-4" />
                                  <span>{deadline.contactEmail}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            daysLeft <= 1 ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                            daysLeft <= 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                          }`}>
                            {daysLeft <= 0 ? 'Süresi Doldu' : `${daysLeft} gün kaldı`}
                          </span>
                          <div className="mt-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              deadline.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                              deadline.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                              deadline.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                              'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                              {deadline.priority === 'critical' ? 'Kritik' :
                               deadline.priority === 'high' ? 'Yüksek' :
                               deadline.priority === 'medium' ? 'Orta' : 'Düşük'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {activeTab === 'settings' && renderSettings()}
      </div>
    </div>
  );
};

export default StudentDashboard;